import { useEffect, useContext, useRef, memo, useCallback } from "react";
import GlobalContext from "GlobalContext";
import Overlayer from "components/Overlayer";
import Loader from "components/Loader";

// Qlik internal cell markers — pinned here so a client upgrade is a 1-line fix.
// Two renderers ship two different DOMs:
//   * straight table  -> legacy qv-st renderer, value in a .qv-st-value node
//   * pivot table      -> nebula sn-pivot-table, cells are div[data-testid=...]
//     with the full (untruncated) value in a `title` attribute.
const ST_CELL_SELECTOR = ".qv-st-data-cell";
const ST_VALUE_SELECTOR = ".qv-st-value";
const PIVOT_CELL_SELECTOR =
  '[data-testid="measure-value"],[data-testid="dimension-cell"],[data-testid="title-cell"]';

// Any copyable cell across both renderers.
const CELL_SELECTOR = `${ST_CELL_SELECTOR},${PIVOT_CELL_SELECTOR}`;

// Pull the display value out of a matched cell, whichever renderer produced it.
function cellText(cell) {
  // Straight table keeps its value in a dedicated node.
  const stValue = cell.querySelector(ST_VALUE_SELECTOR);
  if (stValue) return stValue.textContent.trim();
  // Pivot cells expose the full value via `title` — on the cell itself for
  // measures, on an inner label for dimensions. Fall back to visible text.
  const titled = cell.hasAttribute("title") ? cell : cell.querySelector("[title]");
  const title = titled && titled.getAttribute("title");
  return (title || cell.textContent).trim();
}

// Serialises destruction of a cached Qlik object against the next mount of the
// same id. Capability API caches session objects per id; if the next mount's
// visualization.get() runs before the previous close() has settled, Qlik hands
// back the live instance and re-shows it — silently dropping the render-time
// options (noInteraction / noSelections). Keyed by object id, module scope so
// it survives unmount.
const pendingCloses = new Map();

// Events that can originate a selection. contextmenu is deliberately absent so
// the copyCells right-click handler keeps working.
const SELECTION_EVENTS = [
  "pointerdown",
  "mousedown",
  "mouseup",
  "click",
  "dblclick",
  "touchstart",
];

export default memo(function QlikObject({
  id,
  height = "250px",
  showLoader = true,
  showDataView = false,
  noInteraction = false,
  noSelections = false,
  disableInteractivity = false,
  onTapEvent = null,
  maximized = false,
  copyCells = false,
  copiedSuccessMessage = "Скопійовано",
  copiedFailedMessage = "Не вдалося скопіювати",
}) {
  const { app, qlik } = useContext(GlobalContext);
  const el = useRef(null);
  const qViz = useRef(null);
  const toastTimer = useRef(null);
  const toastEl = useRef(null);

  const showToast = useCallback((x, y, text) => {
    // remove any toast still on screen
    clearTimeout(toastTimer.current);
    if (toastEl.current) {
      toastEl.current.remove();
      toastEl.current = null;
    }

    const toast = document.createElement("div");
    toast.className = "qcopy-toast";
    toast.textContent = text;
    toast.style.left = `${x}px`;
    toast.style.top = `${y}px`;
    document.body.appendChild(toast);
    toastEl.current = toast;

    // force reflow so the transition runs
    // eslint-disable-next-line no-unused-expressions
    toast.offsetHeight;
    toast.classList.add("qcopy-toast--show");

    toastTimer.current = setTimeout(() => {
      toast.classList.remove("qcopy-toast--show");
      setTimeout(() => {
        toast.remove();
        if (toastEl.current === toast) toastEl.current = null;
      }, 200);
    }, 900);
  }, []);

  // ---- mount / show / destroy -------------------------------------------
  useEffect(() => {
    let cancelled = false;

    // Wait for any in-flight close() of this same id before asking for it
    // again. Promise.resolve() handles a close() that returns undefined —
    // in that case there is no serialisation, only no harm.
    const prior = pendingCloses.get(id) || Promise.resolve();

    prior.then(() => {
      if (cancelled) return;

      if (id === "CurrentSelections") {
        app.getObject(el.current, id).then((qmodel) => {
          // Unmounted while get() was in flight: cleanup already ran and saw
          // a null ref, so this instance would leak. Close it here instead.
          if (cancelled) {
            try {
              qmodel.close();
            } catch (_) {}
            return;
          }
          qViz.current = qmodel;
        });
      } else {
        app.visualization.get(id).then((qmodel) => {
          if (cancelled) {
            try {
              qmodel.close();
            } catch (_) {}
            return;
          }
          qmodel.show(el.current, { noInteraction, noSelections });
          qViz.current = qmodel;
        });
      }
    });

    return () => {
      cancelled = true;
      if (qViz.current) {
        const p = Promise.resolve(qViz.current.close()).catch(() => {});
        pendingCloses.set(id, p);
        p.then(() => {
          // only clear if nothing newer has been registered for this id
          if (pendingCloses.get(id) === p) pendingCloses.delete(id);
        });
        qViz.current = null;
      }
    };
  }, [app, id, noInteraction, noSelections, el]);

  // ---- noSelections guard ------------------------------------------------
  // Backstop for the case where Qlik re-shows a cached instance and drops the
  // render option. Capture phase fires before Qlik's own handlers; only
  // stopPropagation (never preventDefault) so native scroll and text selection
  // survive. Hover/move/wheel are untouched, so tooltips still work — which is
  // the distinction between noSelections and noInteraction.
  useEffect(() => {
    const node = el.current;
    if (!noSelections || !node) return undefined;

    const swallow = (e) => e.stopPropagation();
    SELECTION_EVENTS.forEach((t) => node.addEventListener(t, swallow, true));

    return () => {
      SELECTION_EVENTS.forEach((t) => node.removeEventListener(t, swallow, true));
    };
  }, [noSelections, el]);

  useEffect(() => {
    if (qViz.current) qlik.resize(id);
  }, [qlik, id, height, maximized]);

  useEffect(() => {
    if (qViz.current) qViz.current.toggleDataView();
  }, [showDataView]);

  // Right-click a table cell to copy its value
  useEffect(() => {
    if (!copyCells) return undefined;
    const node = el.current;
    if (!node) return undefined;
    const wrapper = node.parentNode;

    const handler = async (e) => {
      let target = document.elementFromPoint(e.clientX, e.clientY);

      const overlay = wrapper.querySelector(".chart-overlayer");
      if (overlay && (target === overlay || overlay.contains(target))) {
        const prevPE = overlay.style.pointerEvents;
        overlay.style.pointerEvents = "none";
        target = document.elementFromPoint(e.clientX, e.clientY);
        overlay.style.pointerEvents = prevPE;
      }

      const cell = target && target.closest(CELL_SELECTOR);
      if (!cell || !node.contains(cell)) return;

      const text = cellText(cell);
      if (!text) return;

      e.preventDefault();
      e.stopPropagation();

      try {
        await navigator.clipboard.writeText(text);
        showToast(e.clientX, e.clientY, copiedSuccessMessage);
      } catch (err) {
        console.error("Copy failed", err);
        showToast(e.clientX, e.clientY, copiedFailedMessage);
      }
    };

    wrapper.addEventListener("contextmenu", handler, true);

    return () => {
      wrapper.removeEventListener("contextmenu", handler, true);
      clearTimeout(toastTimer.current);
      if (toastEl.current) {
        toastEl.current.remove();
        toastEl.current = null;
      }
    };
  }, [copyCells, showToast, copiedSuccessMessage, copiedFailedMessage]);

  return (
    <>
      {disableInteractivity && <Overlayer height={height} onTapEvent={onTapEvent} />}
      <div
        id={id}
        ref={el}
        className={`qchart ${maximized ? "maximized" : ""} ${
          copyCells ? "qcopy-enabled" : ""
        } ${noInteraction ? "qlik-no-interaction" : ""}`}
        style={maximized ? {} : { height }}
      >
        {showLoader && <Loader />}
      </div>
    </>
  );
});